import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors,
	useDraggable, useDroppable, pointerWithin, type DragStartEvent, type DragEndEvent
} from '@dnd-kit/core';
import {
	Card, CardContent, Badge, Button, useToast,
	Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
	Input, Label, Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Tabs, TabsContent, TabsList, TabsTrigger
} from '@hiring-intelligence/ui';
import { sourcingApi, SourcedCandidate } from '@/services/api';
import { PIPELINE_STEPS, REJECTED_BY_LABELS, getStepStatus } from './CandidatePipelineCard';
import { OutreachPanel } from './OutreachPanel';
import { InterviewNotesPanel } from './InterviewNotesPanel';
import { CVGenerationPanel } from './CVGenerationPanel';
import { InterviewQuestionsPanel } from './InterviewQuestionsPanel';
import { Loader2, ExternalLink, Eye, Undo2, Ban, UserCheck, UserX, XCircle, CheckCircle2 } from 'lucide-react';

/**
 * Kanban view of the saved-candidates pipeline. Columns are DERIVED stages, not raw
 * status: `recommended` is a boolean flag (a recommended candidate usually still has
 * status 'sourced'), so terminal statuses win first, then the flag, then In Progress.
 *
 * The cards get the SAME treatment as the list rows — pipeline step chips, status
 * badges and the state-based action buttons (Recommend/Hire/Reject/undo…) — just in
 * card form. Preview is an explicit button, like the list; clicking the card body
 * opens the pipeline panels popup (Outreach / Interview Questions / Interview Notes /
 * CV Generation — the list row's expanded section). Card buttons stopPropagation so
 * they never trigger the popup. Drag-and-drop maps to the same per-transition
 * sourcing endpoints.
 */

type Stage = 'in_progress' | 'recommended' | 'hired' | 'rejected' | 'withdrawn';

const STAGES: { key: Stage; label: string; accent: string }[] = [
	{ key: 'in_progress', label: 'In Progress', accent: 'border-t-blue-400' },
	{ key: 'recommended', label: 'Recommended', accent: 'border-t-emerald-400' },
	{ key: 'hired', label: 'Hired', accent: 'border-t-green-500' },
	{ key: 'rejected', label: 'Rejected', accent: 'border-t-red-400' },
	{ key: 'withdrawn', label: 'Withdrawn', accent: 'border-t-slate-400' }
];

export function getCandidateStage(c: SourcedCandidate): Stage {
	if (c.status === 'hired') return 'hired';
	if (c.status === 'rejected') return 'rejected';
	if (c.status === 'withdrawn') return 'withdrawn';
	if (c.recommended) return 'recommended';
	return 'in_progress';
}

interface CardActions {
	preview: (c: SourcedCandidate) => void;
	openPanels: (c: SourcedCandidate) => void;
	recommend: (c: SourcedCandidate) => void;
	unrecommend: (c: SourcedCandidate) => void;
	withdraw: (c: SourcedCandidate) => void;
	hire: (c: SourcedCandidate) => void;
	reject: (c: SourcedCandidate) => void;
	unhire: (c: SourcedCandidate) => void;
	unreject: (c: SourcedCandidate) => void;
	remove: (c: SourcedCandidate) => void;
}

function KanbanCandidateCard({ candidate, actions, dragging }: {
	candidate: SourcedCandidate;
	actions: CardActions;
	dragging?: boolean;
}) {
	const canRecommend = getStepStatus('recommend', candidate) === 'active';
	const isRecommended = candidate.recommended;
	const isHired = candidate.status === 'hired';
	const isWithdrawn = candidate.status === 'withdrawn';
	const isRejected = candidate.status === 'rejected';
	const isPendingLookup = candidate.name === 'Pending lookup...';
	const isManualCandidate = candidate.signalhireId?.startsWith('manual-');

	// Buttons live inside a draggable — stop propagation so a click never starts a drag.
	const act = (fn: () => void) => (e: React.MouseEvent) => {
		e.stopPropagation();
		fn();
	};

	return (
		<Card className={`overflow-hidden cursor-grab active:cursor-grabbing transition-shadow ${dragging ? 'shadow-lg' : 'hover:shadow-sm'} ${isWithdrawn ? 'opacity-60' : ''}`}>
			<CardContent className="p-3 space-y-2">
				{/* Header: name + title, LinkedIn on the right */}
				<div className="flex items-start justify-between gap-1">
					<div className="min-w-0 flex-1">
						<p className="flex items-center gap-1.5 text-sm font-medium">
							<span className={`truncate ${isPendingLookup ? 'italic text-muted-foreground font-normal' : ''}`}>
								{isPendingLookup ? 'Looking up profile...' : candidate.name}
							</span>
							{isPendingLookup && <Loader2 className="h-3 w-3 animate-spin shrink-0 text-primary" />}
						</p>
						<p className="text-xs text-muted-foreground truncate">
							{[candidate.currentTitle, candidate.currentCompany].filter(Boolean).join(' at ') || '—'}
						</p>
					</div>
					{candidate.linkedinUrl && (
						<button
							type="button"
							onClick={act(() => window.open(candidate.linkedinUrl, '_blank', 'noopener,noreferrer'))}
							className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
							title="Open LinkedIn"
						>
							<ExternalLink className="h-3.5 w-3.5" />
						</button>
					)}
				</div>

				{/* Status badges — same derivations as the list row */}
				<div className="flex flex-wrap gap-1">
					{isManualCandidate && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-600">Manual</Badge>}
					{isHired && <Badge variant="success" className="text-[10px]">Hired</Badge>}
					{isRejected && (
						<Badge variant="outline" className="text-[10px] text-red-600">
							Rejected{candidate.rejectedBy ? ` · by ${REJECTED_BY_LABELS[candidate.rejectedBy]}` : ''}
						</Badge>
					)}
					{isWithdrawn && <Badge variant="outline" className="text-[10px] text-red-600 border-red-300">Withdrawn</Badge>}
					{isRecommended && !isHired && <Badge variant="success" className="text-[10px]">Recommended</Badge>}
					{!isHired && !isRejected && !isWithdrawn && !isRecommended && !canRecommend && (
						<Badge variant="outline" className="text-[10px]">Pipeline Incomplete</Badge>
					)}
				</div>
				{isRejected && candidate.rejectedReason && (
					<p className="text-[11px] text-red-600 line-clamp-2" title={candidate.rejectedReason}>
						{candidate.rejectedReason}
					</p>
				)}

				{/* Pipeline step chips (icon-only, same colors as the list) */}
				<div className="flex items-center">
					{PIPELINE_STEPS.map((step, i) => {
						const status = getStepStatus(step.key, candidate);
						const Icon = step.icon;
						return (
							<div key={step.key} className="flex items-center">
								<div
									className={`flex items-center rounded-md p-1 transition-colors ${
										status === 'done'
											? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
											: status === 'active'
												? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
												: 'bg-muted text-muted-foreground'
									}`}
									title={step.label}
								>
									{status === 'done' ? <CheckCircle2 className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
								</div>
								{i < PIPELINE_STEPS.length - 1 && (
									<div className={`mx-0.5 h-px w-2 ${status === 'done' ? 'bg-green-400' : 'bg-muted-foreground/30'}`} />
								)}
							</div>
						);
					})}
				</div>

				{/* State-based actions + Preview — mirrors the list row's right side */}
				<div className="flex flex-wrap items-center gap-1 border-t pt-2">
					{isHired ? (
						<Button variant="ghost" size="sm" className="h-7 px-2 text-orange-600" onClick={act(() => actions.unhire(candidate))} title="Revert to Recommended">
							<Undo2 className="h-3.5 w-3.5" />
						</Button>
					) : isRejected ? (
						<Button variant="ghost" size="sm" className="h-7 px-2 text-orange-600" onClick={act(() => actions.unreject(candidate))} title="Revert rejection">
							<Undo2 className="h-3.5 w-3.5" />
						</Button>
					) : isWithdrawn ? null : isRecommended ? (
						<>
							<Button size="sm" className="h-7 bg-green-600 px-2 text-white hover:bg-green-700" onClick={act(() => actions.hire(candidate))}>
								<UserCheck className="mr-1 h-3.5 w-3.5" /> Hire
							</Button>
							<Button variant="ghost" size="sm" className="h-7 px-2 text-orange-600" onClick={act(() => actions.unrecommend(candidate))} title="Unrecommend">
								<Undo2 className="h-3.5 w-3.5" />
							</Button>
							<Button variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={act(() => actions.withdraw(candidate))} title="Withdraw">
								<Ban className="h-3.5 w-3.5" />
							</Button>
							<Button variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={act(() => actions.reject(candidate))} title="Reject candidate">
								<UserX className="h-3.5 w-3.5" />
							</Button>
						</>
					) : canRecommend ? (
						<>
							<Button size="sm" className="h-7 px-2" onClick={act(() => actions.recommend(candidate))}>
								Recommend
							</Button>
							<Button variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={act(() => actions.remove(candidate))} title="Unsave (put back)">
								<Undo2 className="h-3.5 w-3.5" />
							</Button>
							<Button variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={act(() => actions.reject(candidate))} title="Reject candidate">
								<UserX className="h-3.5 w-3.5" />
							</Button>
						</>
					) : (
						<>
							{isPendingLookup ? (
								<Button variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={act(() => actions.remove(candidate))} title="Cancel Lookup">
									<XCircle className="h-3.5 w-3.5" />
								</Button>
							) : (
								<>
									<Button variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={act(() => actions.remove(candidate))} title="Unsave (put back)">
										<Undo2 className="h-3.5 w-3.5" />
									</Button>
									<Button variant="ghost" size="sm" className="h-7 px-2 text-red-600" onClick={act(() => actions.reject(candidate))} title="Reject candidate">
										<UserX className="h-3.5 w-3.5" />
									</Button>
								</>
							)}
						</>
					)}
					<Button variant="outline" size="sm" className="ml-auto h-7 gap-1 px-2" onClick={act(() => actions.preview(candidate))}>
						<Eye className="h-3.5 w-3.5" /> Preview
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}

function DraggableCandidateCard({ candidate, actions }: { candidate: SourcedCandidate; actions: CardActions }) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: candidate.id,
		data: { stage: getCandidateStage(candidate) }
	});
	return (
		<div
			ref={setNodeRef}
			{...attributes}
			{...listeners}
			onClick={() => actions.openPanels(candidate)}
			className={isDragging ? 'opacity-40' : ''}
		>
			<KanbanCandidateCard candidate={candidate} actions={actions} />
		</div>
	);
}

function KanbanColumn({ stage, label, accent, candidates, actions }: {
	stage: Stage; label: string; accent: string;
	candidates: SourcedCandidate[]; actions: CardActions;
}) {
	const { setNodeRef, isOver } = useDroppable({ id: stage });
	return (
		<div className="flex w-72 shrink-0 flex-col">
			<div className={`flex items-center justify-between px-3 py-2 border-t-4 rounded-t bg-card ${accent}`}>
				<span className="text-sm font-semibold">{label}</span>
				<Badge variant="secondary" className="text-xs">{candidates.length}</Badge>
			</div>
			<div
				ref={setNodeRef}
				className={`flex-1 space-y-2 p-2 rounded-b-md min-h-[220px] transition-colors ${isOver ? 'bg-primary/5 ring-2 ring-primary/30' : 'bg-muted/20'}`}
			>
				{candidates.map((c) => (
					<DraggableCandidateCard key={c.id} candidate={c} actions={actions} />
				))}
				{candidates.length === 0 && (
					<p className="text-xs text-muted-foreground/60 text-center py-4">No candidates</p>
				)}
			</div>
		</div>
	);
}

interface CandidateKanbanBoardProps {
	candidates: SourcedCandidate[];
	hiringRequestId: string;
	onOpenDetail: (candidate: SourcedCandidate) => void;
	onRefresh: () => void;
}

export function CandidateKanbanBoard({ candidates, hiringRequestId, onOpenDetail, onRefresh }: CandidateKanbanBoardProps) {
	const { toast } = useToast();
	const [activeId, setActiveId] = useState<string | null>(null);

	const [hireTarget, setHireTarget] = useState<SourcedCandidate | null>(null);
	const [hireStartDate, setHireStartDate] = useState('');
	const [rejectTarget, setRejectTarget] = useState<SourcedCandidate | null>(null);
	const [rejectReason, setRejectReason] = useState('');
	const [rejectedByValue, setRejectedByValue] = useState<'us' | 'company' | 'candidate'>('us');

	// Pipeline panels popup (card click) — same tabs as the list row's expanded section.
	const [panelsTarget, setPanelsTarget] = useState<SourcedCandidate | null>(null);
	const [panelsTab, setPanelsTab] = useState('outreach');

	// Vault contacts for the Outreach panel, fetched only while the popup is open.
	const { data: vaultResponse } = useQuery({
		queryKey: ['candidate-vault-detail', panelsTarget?.signalhireId],
		queryFn: () => sourcingApi.getCandidateVaultDetail(panelsTarget!.signalhireId),
		enabled: !!panelsTarget?.signalhireId
	});
	const candidatePhones = (vaultResponse?.data?.contacts || [])
		.filter((c: any) => c.type === 'phone')
		.map((c: any) => ({ value: c.value, subType: c.subType }));
	const candidateEmails = (vaultResponse?.data?.contacts || [])
		.filter((c: any) => c.type === 'email')
		.map((c: any) => ({ value: c.value, subType: c.subType }));

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor)
	);

	const grouped = STAGES.reduce((acc, s) => {
		acc[s.key] = candidates.filter((c) => getCandidateStage(c) === s.key);
		return acc;
	}, {} as Record<Stage, SourcedCandidate[]>);

	// Direct moves that need no dialog — each transition has its own endpoint.
	const moveMutation = useMutation({
		mutationFn: async ({ candidate, to }: { candidate: SourcedCandidate; to: Stage }) => {
			const from = getCandidateStage(candidate);
			if (to === 'recommended') return sourcingApi.selectForInterview(candidate.id);
			if (to === 'withdrawn') return sourcingApi.withdrawCandidate(candidate.id);
			if (to === 'in_progress') {
				if (from === 'recommended') return sourcingApi.unrecommendCandidate(candidate.id);
				if (from === 'hired') return sourcingApi.unhireCandidate(candidate.id);
				if (from === 'rejected') return sourcingApi.unrejectCandidate(candidate.id);
			}
			throw new Error('Unsupported move');
		},
		onSuccess: () => {
			onRefresh();
			toast({ title: 'Candidate updated' });
		},
		onError: (err: Error) => {
			toast({ title: 'Cannot update candidate', description: err.message, variant: 'destructive' });
		}
	});

	// Unsave / cancel-lookup (soft remove from this pipeline).
	const removeMutation = useMutation({
		mutationFn: (candidateId: string) => sourcingApi.removeCandidate(candidateId),
		onSuccess: () => {
			onRefresh();
			toast({ title: 'Removed', description: 'Candidate removed from this hiring request.' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	const hireMutation = useMutation({
		mutationFn: () => sourcingApi.hireCandidate(hireTarget!.id, hireStartDate),
		onSuccess: () => {
			setHireTarget(null);
			onRefresh();
			toast({ title: 'Hired', description: 'Candidate marked as hired. Congratulations!' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	const rejectMutation = useMutation({
		mutationFn: () => sourcingApi.rejectCandidate(rejectTarget!.id, { reason: rejectReason.trim(), rejectedBy: rejectedByValue }),
		onSuccess: () => {
			setRejectTarget(null);
			onRefresh();
			toast({ title: 'Rejected', description: 'Candidate has been rejected.' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	// Shared by the card buttons AND drag-and-drop — one behavior, two entry points.
	const actions: CardActions = {
		preview: onOpenDetail,
		openPanels: (c) => {
			setPanelsTab('outreach');
			setPanelsTarget(c);
		},
		recommend: (c) => moveMutation.mutate({ candidate: c, to: 'recommended' }),
		unrecommend: (c) => moveMutation.mutate({ candidate: c, to: 'in_progress' }),
		unreject: (c) => moveMutation.mutate({ candidate: c, to: 'in_progress' }),
		unhire: (c) => {
			if (window.confirm(`Revert ${c.name} from Hired back to Recommended?`)) {
				moveMutation.mutate({ candidate: c, to: 'in_progress' });
			}
		},
		withdraw: (c) => {
			if (window.confirm(`Withdraw ${c.name} from this hiring request?`)) {
				moveMutation.mutate({ candidate: c, to: 'withdrawn' });
			}
		},
		hire: (c) => {
			setHireStartDate('');
			setHireTarget(c);
		},
		reject: (c) => {
			setRejectReason('');
			setRejectedByValue('us');
			setRejectTarget(c);
		},
		remove: (c) => {
			const message = c.name === 'Pending lookup...'
				? 'Cancel this failed lookup and remove it from pipeline?'
				: `Remove ${c.name} from this hiring request?`;
			if (window.confirm(message)) removeMutation.mutate(c.id);
		}
	};

	const activeCandidate = activeId ? candidates.find((c) => c.id === activeId) : null;
	const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));

	const handleDragEnd = (e: DragEndEvent) => {
		setActiveId(null);
		const { active, over } = e;
		if (!over) return;
		const candidate = candidates.find((c) => c.id === String(active.id));
		if (!candidate) return;
		const from = getCandidateStage(candidate);
		const to = over.id as Stage;
		if (from === to) return;

		// Terminal columns can't be skipped over: bring the candidate back first.
		if (from === 'withdrawn') {
			toast({ title: 'Not supported', description: 'Withdrawn candidates cannot be moved back — re-add them from the vault if needed.', variant: 'destructive' });
			return;
		}
		if (from === 'hired' && to !== 'in_progress') {
			toast({ title: 'Unhire first', description: 'Move the candidate to In Progress (unhire) before another change.', variant: 'destructive' });
			return;
		}
		if (from === 'rejected' && to !== 'in_progress') {
			toast({ title: 'Unreject first', description: 'Move the candidate to In Progress (unreject) before another change.', variant: 'destructive' });
			return;
		}

		if (to === 'hired') {
			// Same gate as Recommend: the Standard CV must be generated first.
			if (!candidate.hasGeneratedStandardCV) {
				toast({ title: 'Pipeline incomplete', description: 'Generate the Standard CV first — hiring has the same requirement as recommending.', variant: 'destructive' });
				return;
			}
			return actions.hire(candidate);
		}
		if (to === 'rejected') return actions.reject(candidate);
		if (to === 'withdrawn') return actions.withdraw(candidate);
		if (to === 'in_progress' && from === 'hired') return actions.unhire(candidate);
		moveMutation.mutate({ candidate, to });
	};

	return (
		<>
			<DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
				<div className="flex gap-3 overflow-x-auto pb-2">
					{STAGES.map((s) => (
						<KanbanColumn
							key={s.key}
							stage={s.key}
							label={s.label}
							accent={s.accent}
							candidates={grouped[s.key]}
							actions={actions}
						/>
					))}
				</div>
				<DragOverlay>{activeCandidate ? <KanbanCandidateCard candidate={activeCandidate} actions={actions} dragging /> : null}</DragOverlay>
			</DndContext>

			{/* Pipeline panels popup — the list row's expanded tabs, opened by card click */}
			<Dialog open={!!panelsTarget} onOpenChange={(open) => { if (!open) setPanelsTarget(null); }}>
				<DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>{panelsTarget?.name}</DialogTitle>
						<DialogDescription>
							{[panelsTarget?.currentTitle, panelsTarget?.currentCompany].filter(Boolean).join(' at ') || 'Pipeline actions for this candidate'}
						</DialogDescription>
					</DialogHeader>
					{panelsTarget && (
						<Tabs value={panelsTab} onValueChange={setPanelsTab}>
							<TabsList className="w-full justify-start">
								<TabsTrigger value="outreach">Outreach</TabsTrigger>
								<TabsTrigger value="questions">Interview Questions</TabsTrigger>
								<TabsTrigger value="interview">Interview Notes</TabsTrigger>
								<TabsTrigger value="cv">CV Generation</TabsTrigger>
							</TabsList>

							<TabsContent value="outreach">
								<OutreachPanel
									sourcedCandidateId={panelsTarget.id}
									hiringRequestId={hiringRequestId}
									candidatePhones={candidatePhones}
									candidateEmails={candidateEmails}
									onStatusChange={onRefresh}
								/>
							</TabsContent>

							<TabsContent value="questions">
								<InterviewQuestionsPanel
									sourcedCandidateId={panelsTarget.id}
									hiringRequestId={hiringRequestId}
								/>
							</TabsContent>

							<TabsContent value="interview">
								<InterviewNotesPanel
									sourcedCandidateId={panelsTarget.id}
									hiringRequestId={hiringRequestId}
									onNotesChange={onRefresh}
								/>
							</TabsContent>

							<TabsContent value="cv">
								<CVGenerationPanel
									sourcedCandidateId={panelsTarget.id}
									hiringRequestId={hiringRequestId}
									hasCvFile={!!panelsTarget.cvFileUrl}
									hasSignalHireData={!!panelsTarget.signalhireId && !panelsTarget.signalhireId.startsWith('manual-')}
									cvOriginalName={panelsTarget.cvOriginalName}
									onCvUploaded={onRefresh}
								/>
							</TabsContent>
						</Tabs>
					)}
				</DialogContent>
			</Dialog>

			{/* Hire dialog (same contract as the list card: start date required) */}
			<Dialog open={!!hireTarget} onOpenChange={(open) => { if (!open) setHireTarget(null); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Confirm Hire</DialogTitle>
						<DialogDescription>
							Mark <strong>{hireTarget?.name}</strong> as hired for this position.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label>Start Date</Label>
							<Input type="date" value={hireStartDate} onChange={(e) => setHireStartDate(e.target.value)} />
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setHireTarget(null)}>Cancel</Button>
						<Button
							onClick={() => hireMutation.mutate()}
							disabled={!hireStartDate || hireMutation.isPending}
							className="bg-green-600 hover:bg-green-700"
						>
							{hireMutation.isPending ? 'Processing...' : 'Confirm Hire'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Reject dialog (reason + who rejected, same as the list card) */}
			<Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) setRejectTarget(null); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Reject Candidate</DialogTitle>
						<DialogDescription>
							Mark <strong>{rejectTarget?.name}</strong> as rejected. Record who rejected and why.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label>Rejected by</Label>
							<Select value={rejectedByValue} onValueChange={(v) => setRejectedByValue(v as 'us' | 'company' | 'candidate')}>
								<SelectTrigger>
									<SelectValue placeholder="Select who rejected" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="us">Us</SelectItem>
									<SelectItem value="company">Company</SelectItem>
									<SelectItem value="candidate">Candidate</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="grid gap-2">
							<Label>Reason</Label>
							<Textarea
								value={rejectReason}
								onChange={(e) => setRejectReason(e.target.value)}
								placeholder="Why is this candidate being rejected?"
								rows={3}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
						<Button
							variant="destructive"
							onClick={() => rejectMutation.mutate()}
							disabled={!rejectReason.trim() || rejectMutation.isPending}
						>
							{rejectMutation.isPending ? 'Processing...' : 'Confirm Reject'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
